// @ts-strict-ignore
import { EventService } from 'rainbow-web-sdk/services/core/event.service';
import { Company } from 'rainbow-web-sdk/models/company.model';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { UserInfoService } from 'rainbow-web-sdk/services/core/userInfo.service';
import { ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { Service } from 'rainbow-web-sdk/services/service';
import { MD5 } from '../utilities/utilities.service';

export const COMPANY_SVC = 'CompanyService';
export class CompanyService extends Service {

    private eventService: EventService = EventService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private authService: AuthService = AuthService.getInstance();
    private userInfoService: UserInfoService = UserInfoService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private companies: any = {};

    public static getInstance() {
        return Service.create(COMPANY_SVC, CompanyService);
    }

    public static build() {
        return new CompanyService();
    }

    private constructor() {
        super();
        this.xmppService.subscribeToConnectionEvents((event: RBEvent) => {
            if (event.name === 'ON_XMPP_DISCONNECTED' && event.data.reason === 'LOGOUT') {
                this.companies = {};
            }
        });

        setTimeout(() => { this.configure(); }, 500);
    }

    private configure() {
        this.eventService.subscribe('ON_COMPANY_CHANGE_EVENT', (__event: any, companyId: string) => {
            this.getCompanyById(companyId, true);
        });
    }

    public async getCompanyById(companyId: string, update = false): Promise<Company> {
        try {
            if (!companyId) { throw (new Error('missing companyId')); }

            // Try to get company from cache
            let company: Company = this.companies[companyId];
            if (company && !update) {
                this.logger.info("[companyService] getCompanyById (cache) " + companyId + " - success");
                return company;
            }

            // Get company information from server
            const headers = this.authService.getRequestHeader();
            let url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/companies/${companyId}`;
            url += "?selectedThemeObj=true";
            const responseData: any = await fetch(url, { method: 'GET', headers });
            if (!responseData.ok) { throw new Error(responseData.statusText); }
            const companyData: any = await responseData.json();

            // Update or create company
            if (company) {
                company.updateFromData(companyData.data);
                this.logger.info(`getCompanyById (update) ${companyId} - success`);
            }
            else {
                company = Company.createFromData(companyData.data);
                this.companies[company.id] = company;
                this.logger.info(`getCompanyById ${companyId} - success`);
            }

            // Load asynchronously company logo and banner
            this.getCompanyLogo(company);
            this.getCompanyBanner(company);
            return company;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "createWebinar");
            const errorMessage = `getCompanyById -- FAILURE -- ${errorInfo.message} -- ${errorInfo.error.errorDetails}`;
            this.logger.error(errorMessage);
            throw (new Error(errorMessage));
        }
    }

    public async searchCompanies(searchText: string, hasBP: string = null, isBP: boolean = null, bpType: string = null) {
        try {
            let url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/companies?name=${encodeURIComponent(searchText)}&format=full`;
            if (hasBP !== undefined && hasBP !== null) { url += "&hasBP=" + hasBP; }
            if (isBP !== undefined && isBP !== null) { url += "&isBP=" + isBP; }
            if (bpType) { url += "&bpType=" + bpType; }
            const headers = this.authService.getRequestHeader();
            const responseData: any = await fetch(url, { method: 'GET', headers });
            if (!responseData.ok) { throw new Error(responseData.statusText); }
            const companiesData: any = await responseData.json();
            const companies = companiesData.data.reduce((result: any[], companyData: any) => {
                if (companyData.name !== "Default" && companyData.name !== "Rainbow" && companyData.status === "active") {
                    let company = this.companies[companyData.id];
                    if (!company) {
                        company = Company.createFromData(companyData);
                        this.companies[company.id] = company;
                        this.getCompanyLogo(company);
                    }
                    result.push(company);
                }
                return result;
            }, []);
            this.logger.info("searchCompanies with criteria '" + searchText + "' - success - found " + companiesData.length + " companies : returns " + companies.length + " companies");
            return companies;
        }
        catch (error) {
            const errorMessage = `searchCompanies -- FAILURE -- ${error.message}`;
            this.logger.error(errorMessage);
            throw (new Error(errorMessage));
        }
    }

    public async getCompanyAdminsData(companyId: string) {
        try {
            let url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/companies/${companyId}/administrators`;
            url += "?selectedThemeObj=true";
            const headers = this.authService.getRequestHeader();
            const responseData: any = await fetch(url, { method: 'GET', headers });
            if (!responseData.ok) { throw new Error(responseData.statusText); }
            const data: any = (await responseData.json()).data;
            return data;
        }
        catch (error) {
            const errorMessage = `getCompanyAdminsData -- FAILURE -- ${error.message}`;
            this.logger.error(errorMessage);
            throw (new Error(errorMessage));
        }
    }

    public async getCompanyUsers(companyId: string, pageSize: number) {
        try {
            const url = `${config.restServerUrl}/api/rainbow/search/v1.0/users?companyId=${companyId}&limit=${pageSize}`;
            const headers = this.authService.getRequestHeader();
            const responseData: any = await fetch(url, { method: 'GET', headers });
            if (!responseData.ok) { throw new Error(responseData.statusText); }
            /*const data: any = await responseData.json();
            /$http({ method: "GET", url: serverUrl, headers: authService.getRequestHeader() })
                .then(function success(response) {
                    var data = response.data.data;
                    $log.info("[companyService] getUsersInCompany success)");
                    var users = [];
                    data.forEach(function(userData) {
                        var contact = Contact.createFromData(userData);
                        users.push(contact);
                    });
                    resolve({ users: users });
                }, function failure(response) {
                    var error = errorHelperService.handleError(response);
                    reject(error);
                    $log.error("[companyService] " + errorHelperService.getErrorFullMessage(response, "getUsersInCompany"));
                });*/
        }
        catch (error) {
            const errorMessage = `getCompanyUsers -- FAILURE -- ${error.message}`;
            this.logger.error(errorMessage);
            throw (new Error(errorMessage));
        }
    }

    public async getCompanyLogo(company: Company): Promise<Company> {
        try {
            const companyColor = this.userInfoService.computeUserColor(company.name);
            const companyInitials = company.name.substr(0, 1);
            company.logo = this.userInfoService.getAvatarImage(company.id, companyInitials, companyColor.color, 130, company.lastAvatarUpdateDate);
            this.eventService.publish("ON_COMPANY_LOGO_UPDATED", company.id);
            this.logger.info("[getCompanyLogo] - " + company.getNameForLogs() + " - success");
            company.sendChangeEvent('logo');
            return company;
        }
        catch (error) {
            throw (new Error(`getCompanyLogo -- FAILURE -- ${error.message}`));
        }
    }

    public async getCompanyBanner(company: Company): Promise<Company> {
        return new Promise<Company>((resolve) => {
            const image = new Image();

            image.onload = () => {
                company.banner = image;
                this.eventService.publish("ON_COMPANY_BANNER_UPDATED", company.id);
                this.logger.info("[getCompanyBanner] - " + company.getNameForLogs() + " - success");
                resolve(company);
            };

            image.onerror = () => {
                this.logger.error("[companyService] Load banner for " + company.getNameForLogs() + " failure");
                resolve(company);
            };

            // Load the image

            let imgSrc = config.sdk ? null : "/cacheV2/images/company-banner-01.svg";
            if (company.lastBannerUpdateDate) {
                const serverURL = config.webservices.protocol + "://" + config.webservices.currentServer;
                imgSrc = serverURL + "/api/banner/" + company.id + "?size=831";
                imgSrc += "&update=" + MD5(company.lastBannerUpdateDate);
            }
            if (imgSrc) { image.src = imgSrc; }
            else resolve(company);
        });
    }

    public async getImageMimeType(imageUrl) {
        try {
            if (!imageUrl) { return null; }
            const responseData: any = await fetch(imageUrl);
            if (!responseData.ok) { throw new Error(responseData.statusText); }
            const blob = await responseData.blob();
            return blob.type; // Image Content-Type (e.g. "image/png")
        }
        catch (error) {
            const errorMessage = `getImageMimeType -- FAILURE -- ${error.message}`;
            this.logger.error(errorMessage);
            return null;
        }
    }

    public async getCompanyFeatures(companyId: string): Promise<any> {
        try {
            const features = { alertNotifAllowed: false, webinarAccess: false, webinarMaxSpeakers: 10 };
            const url = `${config.restServerUrl}/api/rainbow/subscription/v1.0/companies/${companyId}/subscriptions/features`;
            const headers = this.authService.getRequestHeader();
            const responseData: any = await fetch(url, { method: 'GET', headers });
            if (!responseData.ok) { throw new Error(responseData.statusText); }
            const data: any = (await responseData.json()).data;

            const featureAlertNotifAllowed = data.find((feat: any) => { return feat.featureUniqueRef === 'ALERT_NOTIFICATIONS_ALLOWED'; });
            if (featureAlertNotifAllowed) { features.alertNotifAllowed = featureAlertNotifAllowed.isEnabled; }

            const featureWebinarAccess = data.find((feat: any) => { return feat.featureUniqueRef === 'WEBINAR_ACCESS'; });
            if (featureWebinarAccess) { features.webinarAccess = featureWebinarAccess.isEnabled; }

            const featureWebinarMaxSpeakers = data.find((feat: any) => { return feat.featureUniqueRef === 'WEBINAR_MAX_ORGANIZERS'; });
            if (featureWebinarMaxSpeakers) {
                features.webinarMaxSpeakers = featureWebinarMaxSpeakers.limitMax;
            }

            return features;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getCompanyFeatures');
            this.logger.warn(`[companyService] ${errorInfo.message}`);
            return {};
        }
    }
}
